import type { User, OrganizationRole, WorkspaceRole } from "./user"
import type { AIDraftToolSupport } from "./aiDraft"
import type {
  AgentType,
  AgentResponseMode,
  UnifiedTool,
  HumanHandoffConfig,
  AgentLLMModelSettingsMap,
  OutputPresentationPolicy,
} from "./agent"
import type { Brand } from "./brand"
import type {
  Conversation as UIConversation,
  History as UIHistory,
} from "@f2e/ui/chat"
import type {
  UploadStatus,
  FileBadgeData as SharedFileBadgeData,
} from "@f2e/ui/components/Attachment"

export type { Response, Pagination } from "./network"
export type {
  ServerConfig,
  SystemConfig,
  ProviderBrand,
  PageInfo,
} from "./system"
export { Permission, Scope } from "./permissions"
export * from "./user"
export * from "./connection"
export * from "./agent"
export * from "./aiDraft"
export * from "./eval"
export * from "./evaluator"
export * from "./token"
export * from "./copilot"
export * from "./brand"
export * from "./clientChannel"
export * from "./apiKey"
export * from "./text2speechTool"

import type { Identity, ReasoningStep } from "@f2e/shared/types"
import {
  FileAttachmentScope,
  FileAttachmentType,
  IdentityKind,
  ReasoningStepType,
} from "@f2e/shared/types"
import type { ConversationType as ChatConversationType } from "@f2e/ui/chat"
export { ConversationType, ConversationScore } from "@f2e/ui/chat"
export type {
  ConversationContext,
  ConversationGroup,
  ConversationMeta,
  ConversationSettings,
  FeedbackSystemType,
} from "@f2e/ui/chat"

// Re-export shared types
export type { ReasoningStep }
export {
  FileAttachmentScope,
  FileAttachmentType,
  IdentityKind,
  ReasoningStepType,
}
export type { Identity } from "@f2e/shared/types"
export type { UploadStatus }

export interface Organization {
  id: string
  name: string
  avatar?: string
  owner: User
  myRole: OrganizationRole
  myPermissions: Set<string>
}

// Provenance values returned by historical conversations, citations and attachments.
// Retired values stay for rendering saved records; this enum is not a connection allowlist.
export const enum ContextProvider {
  Internet = "SEARCH",
  Agent = "AGENT",
  Github = "GITHUB", // Retired: historical display only.
  Notion = "NOTION",
  Linear = "LINEAR", // Retired: historical display only.
  GoogleDrive = "GOOGLE_DRIVE",
  Jira = "JIRA", // Retired: historical display only.
  Website = "WEBSITE",
  Confluence = "CONFLUENCE", // Retired: historical display only.
  Bitbucket = "BITBUCKET", // Retired: historical display only.
}

export const ContextProviderDisplayName: Record<string, string> = {
  [ContextProvider.Internet]: "Search",
  [ContextProvider.Agent]: "Agent",
  [ContextProvider.Github]: "Github",
  [ContextProvider.Notion]: "Notion",
  [ContextProvider.Linear]: "Linear",
  [ContextProvider.GoogleDrive]: "Google Drive",
  [ContextProvider.Jira]: "Jira",
  [ContextProvider.Confluence]: "Confluence",
  [ContextProvider.Bitbucket]: "Bitbucket",
  [ContextProvider.Website]: "Website",
}

export interface QueryRecordImage {
  url: string
  hash: string
}

export interface QueryRecord {
  id: number
  name: string
  url: string
  provider: ContextProvider
  images: QueryRecordImage[]
  content: string
  locStart?: ChunkLocation
  locEnd?: ChunkLocation
  publishedAt: string
}

export interface UserQuery {
  id: number
  sources: QuerySourceMeta[]
  externalSearch: boolean
  records: QueryRecord[]
}

export interface Workspace {
  id: string
  name: string
  default: boolean
  owner: User
  isJoined: boolean
  myRole: WorkspaceRole
  myPermissions: Set<string>
  createdAt: string
  updatedAt: string
  members?: User[]
  organizationId?: string
  brand?: Brand
  apiKeyClientsBypassWhitelist?: boolean
  maskEndUserIdentity?: boolean
  contactEmail?: string | null
  contactEmailDefault?: string | null
  monthlyEndUserCreditCap?: number | null
  endUserCreditCapBlockMessageI18n?: Partial<
    Record<"en" | "zh-TW" | "ja", string>
  > | null
  endUserCreditCapBlockMessageDefaultI18n?: Partial<
    Record<"en" | "zh-TW" | "ja", string>
  >
}

export interface WorkspaceProfile {
  id: string
  name: string
}

export interface AgentProfile {
  id: string
  name: string
  type: AgentType
  systemPrompt: string
}

export interface QuerySourceMeta {
  id: string
  name: string
  provider: string
}

export type FeedbackTag = "system" | "custom"

export interface ConversationFeedback {
  id: number
  tag: FeedbackTag
  type: string
  identity: Identity
  content?: string
  createdAt: string
}

export type HistoryMeta = Record<string, never>

// Web app extends shared UI conversation model with identity from backend payload.
export interface Conversation extends UIConversation {
  identity?: Identity
}

export interface History extends UIHistory {
  canReply?: boolean
  replyDisabledReason?: string
  clientUserId?: string
  aiDraftToolSupport?: AIDraftToolSupport
}

export interface CopilotConversationContext {
  type: ChatConversationType
  question: string
  files?: FileBadgeData[]
  groupId?: string
}

export interface ChunkLocation {
  line: number
  position: number
}

export interface QueryBundle {
  question: string
  keywords: string[]
}

export interface ToolQueryData {
  provider: string
  toolName: string
  arguments: Record<string, string>
}

export interface ContextImage {
  url: string
  name: string
  description?: string
  hash: string
}

export interface ContextObj {
  id: string
  name: string
  url: string
  connectionId: string
  provider: string
  lastUpdated: string
  timeIndexed: string
  content?: string
  images?: ContextImage[]
}

export interface KnowledgeProfile {
  id: string
  name: string
  workspace: WorkspaceProfile
  createdAt: string
  updatedAt: string
}

interface AgentMeta {
  contextObjects?: ContextObj[]
  outputPresentationPolicy?: OutputPresentationPolicy
}

export interface BasicAgentMeta extends AgentMeta {}

export interface Agent<T extends AgentType = AgentType> {
  id: string
  name: string
  description?: string
  creator?: User
  type: T
  workspaceId?: string
  workspace?: Workspace
  systemPrompt: string
  unifiedTools: UnifiedTool[]
  responseMode: AgentResponseMode
  humanHandoff: HumanHandoffConfig
  aiTakeoverGreeting?: string
  suggestedQuestions?: string[]
  llmModel?: string
  llmModelSettings?: AgentLLMModelSettingsMap
  outputPresentationPolicy?: OutputPresentationPolicy
  meta: BasicAgentMeta
  useSearch?: boolean
  createdAt: string
  updatedAt: string
  version: number
  latestVersionNumber?: number
  publishedVersionNumber?: number
}

export type FileBadgeData = SharedFileBadgeData
